import { CommonModule, formatDate } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MaterialModule } from '../../material.module';

import { AppointmentService } from '../appointment.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AppStateService } from '../../app-state.service';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { Router, NavigationEnd } from '@angular/router';
import { Appointment } from '../appointment.model';
import { Subject, filter, takeUntil } from 'rxjs';

@Component({
  selector: 'app-appointments-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialModule,
    MatMenuModule,
    MatIconModule
  ],
  templateUrl: './appointments-list.component.html',
  styleUrls: ['./appointments-list.component.scss']
})
export class AppointmentsListComponent implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  appStateService = inject(AppStateService);
  appointmentService = inject(AppointmentService);
  router = inject(Router);
  snackBar = inject(MatSnackBar);
  
  // Appointment data
  appointments: Appointment[] = [];
  upcomingAppointments: Appointment[] = [];
  plannedAppointments: Appointment[] = [];
  pastAppointments: Appointment[] = [];

  // User identifiers
  clientId: string = '';
  patientId: string = '';

  // UI state
  loading = false;
  loadingStatus = 'Loading your appointments...';

  constructor() {
    const state = this.appStateService.getState();
    this.clientId = state.clientId || '';
    this.patientId = state.patientId || '';
  }

  ngOnInit(): void {
    this.fetchAppointments();

    // Listen for newly created appointments and add them immediately
    console.log('Setting up newAppointment$ subscription');
    this.appointmentService.newAppointment$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((newAppointment) => {
        console.log('Received newAppointment emission:', newAppointment);
        if (newAppointment && newAppointment.appointmentNumber) {
          console.log('Adding new appointment to list:', newAppointment.appointmentNumber);
          this.addNewAppointment(newAppointment);
        }
      });

    // Listen for navigation events to refresh appointments when returning from scheduler
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((event) => {
        const navEvent = event as NavigationEnd;

        // If navigating to this appointments page, refresh the data
        if (navEvent.url.includes('/dashboard')) {
          setTimeout(() => {
            this.fetchAppointments();
          }, 1000); // Longer delay to ensure backend has processed the change
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  /**
   * Fetch appointments from the API
   */
  fetchAppointments(): void {
    // Validate patientId before making API call
    if (!this.patientId || this.patientId.trim() === '') {
      this.showNotification('Session expired. Please log in again.');
      this.loading = false;
      return;
    }

    this.loading = true;
    this.appointmentService.fetchAppointments(this.clientId, this.patientId).subscribe({
      next: (fetchedAppointments: Appointment[]) => {
        console.log('fetchAppointments completed, received:', fetchedAppointments.length);
        console.log('Current appointments count before merge:', this.appointments.length);

        // Merge fetched appointments with any already in the list (e.g. from newAppointment$)
        // Fetched data is authoritative — update existing entries with full server data
        const existingByNumber = new Map(
          this.appointments.map(apt => [apt.appointmentNumber, apt])
        );

        for (const apt of fetchedAppointments) {
          // Always use fetched data (more complete than the booking response)
          existingByNumber.set(apt.appointmentNumber, apt);
        }

        this.appointments = Array.from(existingByNumber.values());

        console.log('After merge, appointments count:', this.appointments.length);

        this.categorizeAppointments();
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.showNotification('Unable to load your appointments. Please try again later.');
      }
    });
  }

  /**
   * Categorize appointments into upcoming, planned, and past
   */
  categorizeAppointments(): void {
    const now = new Date();

    // Filter upcoming appointments (in the future regardless of status, except completed)
    this.upcomingAppointments = this.appointments.filter(appointment => {
      const apptDate = new Date(appointment.appointmentDateTime);
      const status = appointment.appointmentStatus?.toLowerCase();
      // Include future appointments that aren't completed
      const isUpcoming = apptDate >= now &&
                        status !== 'complete' &&
                        status !== 'completed';
      return isUpcoming;
    }).sort((a, b) =>
      new Date(a.appointmentDateTime).getTime() - new Date(b.appointmentDateTime).getTime()
    );

    // Filter planned appointments
    this.plannedAppointments = this.appointments.filter(appointment =>
      appointment.appointmentStatus === 'Planned' ||
      appointment.appointmentStatus === 'planned'
    );

    // Filter past appointments (in the past OR completed even if in future)
    this.pastAppointments = this.appointments.filter(appointment => {
      const apptDate = new Date(appointment.appointmentDateTime);
      const status = appointment.appointmentStatus?.toLowerCase();
      // Include appointments in the past OR completed appointments regardless of date
      const isPast = apptDate < now ||
                    status === 'complete' ||
                    status === 'completed';
      return isPast;
    }).sort((a, b) =>
      new Date(b.appointmentDateTime).getTime() - new Date(a.appointmentDateTime).getTime()
    );
  }

  /**
   * Add a newly created appointment to the list immediately
   * Deduplicates by appointmentNumber to prevent duplicates
   */
  addNewAppointment(newAppointment: any): void {
    console.log('addNewAppointment called with:', newAppointment);
    console.log('Current appointments count:', this.appointments.length);

    // Check if appointment already exists (deduplicate by appointmentNumber)
    const existingIndex = this.appointments.findIndex(
      apt => apt.appointmentNumber === newAppointment.appointmentNumber
    );

    console.log('Existing appointment index:', existingIndex);

    if (existingIndex !== -1) {
      // Update existing appointment (in case of any changes)
      console.log('Updating existing appointment at index:', existingIndex);
      this.appointments[existingIndex] = newAppointment;
    } else {
      // Add new appointment to the list
      console.log('Adding new appointment to list');
      this.appointments.push(newAppointment);
    }

    console.log('Appointments after add:', this.appointments.length);

    // Re-categorize appointments to update upcoming/past lists
    this.categorizeAppointments();

    console.log('Upcoming appointments:', this.upcomingAppointments.length);
    console.log('Past appointments:', this.pastAppointments.length);

    // Show success notification
    this.showNotification('Appointment added successfully! Refreshing list...');

    // Schedule a background refresh to sync with server
    setTimeout(() => {
      console.log('Background refresh triggered');
      this.fetchAppointments();
    }, 1500);
  }

  /**
   * Get icon for appointment status
   */
  getStatusIcon(status: string | undefined, appointment?: any): string {
    // Check if appointment was cancelled based on notes
    if (appointment && this.isAppointmentCancelled(appointment)) {
      return 'cancel';
    }

    // Handle undefined/null status
    if (!status) {
      return 'help';
    }

    // Check if appointment is confirmed
    if (appointment && appointment.confirmed === 'Confirmed' && status.toLowerCase() === 'scheduled') {
      return 'check_circle';
    }

    const icons: any = {
      scheduled: 'event_available',
      broken: 'event_busy',
      complete: 'check_circle',
      unschedlist: 'cancel',
      planned: 'event'
    };
    return icons[status.toLowerCase()] || 'help';
  }

  /**
   * Get color class for appointment status
   */
  getStatusColor(status: string | undefined, appointment?: any): string {
    // Check if appointment was cancelled based on notes
    if (appointment && this.isAppointmentCancelled(appointment)) {
      return 'warn';
    }

    // Handle undefined/null status
    if (!status) {
      return '';
    }

    const colors: any = {
      scheduled: 'primary',
      broken: 'warn',
      unschedlist: 'warn',
      planned: 'accent',
      complete: 'completed',
      completed: 'completed'
    };
    return colors[status.toLowerCase()] || '';
  }


  /**
   * Get display text for appointment status
   */
  getStatusDisplay(status: string | undefined, appointment?: any): string {
    // Check if appointment was cancelled based on notes
    if (appointment && this.isAppointmentCancelled(appointment)) {
      return 'Cancelled';
    }

    // Handle undefined/null status
    if (!status) {
      return 'Unknown';
    }

    // Check if appointment is confirmed
    if (appointment && appointment.confirmed === 'Confirmed' && status.toLowerCase() === 'scheduled') {
      return 'Confirmed';
    }

    const statuses: any = {
      scheduled: 'Scheduled',
      broken: 'Missed',
      unschedlist: 'Cancelled',
      complete: 'Complete',
      completed: 'Completed',
      planned: 'Planned'
    };
    return statuses[status.toLowerCase()] || status || 'Unknown';
  }

  /**
   * Check if appointment was cancelled based on notes
   */
  isAppointmentCancelled(appointment: any): boolean {
    const note = appointment.note || '';
    return note.toLowerCase().includes('cancelled by');
  }

  /**
   * Format date for display
   */
  formatAppointmentDate(date: string): string {
    if (!date) return 'Not scheduled';
    
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', { 
        weekday: 'short',
        month: 'short', 
        day: 'numeric', 
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (e) {
      return date;
    }
  }

  /**
   * Format month for display in date badge
   */
  formatMonth(date: string): string {
    if (!date) return '';
    
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleDateString('en-US', { month: 'short' });
    } catch (e) {
      return '';
    }
  }

  /**
   * Format day for display in date badge
   */
  formatDay(date: string): string {
    if (!date) return '';
    
    try {
      const dateObj = new Date(date);
      return dateObj.getDate().toString();
    } catch (e) {
      return '';
    }
  }

  /**
   * Format time for display
   */
  formatTime(date: string): string {
    if (!date) return '';
    
    try {
      const dateObj = new Date(date);
      return dateObj.toLocaleTimeString('en-US', { 
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch (e) {
      return '';
    }
  }

  /**
   * Navigate to book an appointment
   */
  bookAppointment(): void {
    this.router.navigate(['/dashboard', 'appointment', 'new']);
  }

  /**
   * Confirm an appointment
   */
  confirmAppointment(appointmentNumber: string): void {
    this.loading = true;
    this.loadingStatus = 'Confirming your appointment...';

    this.appointmentService.manageAppointment(this.clientId, this.patientId, appointmentNumber, 'confirm').subscribe({
      next: () => {
        this.loading = false;
        this.showNotification('Your appointment has been confirmed. Thank you!');

        // Optimistically update the local appointment's confirmed status
        const appointment = this.appointments.find(apt => apt.appointmentNumber === appointmentNumber);
        if (appointment) {
          appointment.confirmed = 'Confirmed';
          this.categorizeAppointments();
        }

        // Fetch in background to sync with server (with delay for PMS sync)
        setTimeout(() => {
          this.fetchAppointments();
        }, 1500);
      },
      error: (error) => {
        this.loading = false;
        this.showNotification('Unable to confirm your appointment. Please try again later.');
      }
    });
  }

  /**
   * Cancel an appointment
   */
  cancelAppointment(appointmentNumber: string): void {
    this.loading = true;
    this.loadingStatus = 'Canceling your appointment...';
    
    this.appointmentService.manageAppointment(this.clientId, this.patientId, appointmentNumber, 'cancel').subscribe({
      next: () => {
        this.loading = false;
        this.showNotification('Your appointment has been canceled.');
        this.fetchAppointments();
      },
      error: (error) => {
        this.loading = false;
        this.showNotification('Unable to cancel your appointment. Please try again later.');
      }
    });
  }

  /**
   * Navigate to reschedule an appointment
   */
  rescheduleAppointment(appointmentNumber: string): void {
    this.router.navigate(['/dashboard', 'appointment', appointmentNumber]);
  }

  /**
   * Navigate to schedule a planned appointment
   */
  reschedulePlannedAppointment(appointmentNumber: string): void {
    this.router.navigate(['/dashboard', 'appointment', appointmentNumber]);
  }

  /**
   * Show notification to user
   */
  showNotification(message: string): void {
    this.snackBar.open(message, 'Close', {
      duration: 5000,
    });
  }
}